import { AskAiPanel } from '../modules/vendor/assistant/components/AskAiPanel';
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { TrialEndedWall } from "../modules/vendor/pricing/TrialEndedWall";
import { isOpenWhenExpired, readAccess } from "../components/design-system/plan/access";
import { cn } from "@/lib/utils";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { SHELL_HEIGHT } from '../modules/vendor/assistant/askai-layout';
import { SideBar } from "../common/Header/SideBar";
import { MyProducts } from "../pages/vendor/products/my-products.page";
import { NetworkPage } from "../pages/vendor/products/network.page";
import { NetworkBrandPage } from "../pages/vendor/products/network-brand.page";
import { EditProductV3Page } from "../modules/vendor/edit-product-v3";
import { setCompanyName, setFont, setInitialTheme, setLanguage, setLogo, setOrderButtonAction, setOrderButtonLabel, setPrimaryColor, setProductsOrder, setSecondaryColor, setTextSecondaryColor , setAnalyticsEnabled } from "../redux/vendor/settings/settingsActions";
import Dashboard from "../pages/vendor/dashboard/dashboard.page";
import AgentHomePage from "../pages/vendor/home/agent-home.page";
import AgentChatPage from "../pages/vendor/home/agent-chat.page";
import AgentsPage from "../pages/vendor/home/agents.page";
import ChannelsPage from "../pages/vendor/home/channels.page";
import RoutinesPage from "../pages/vendor/home/routines.page";
import NotificationsPage from "../pages/common/notifications/notifications.page";
import AnalyticsPage from '../pages/vendor/analytics/analytics.page';
import ExportAnalyticsPage from "../pages/vendor/analytics/export-analytics.page";
import { VendorState } from "../redux/vendor/rootReducer";
import Config from "../modules/vendor/settings/models/config.model";
import { SeamlessGenerator } from "../pages/vendor/seamlessGenerator/seamless-generator.page";
import { ImportProductsV2 } from "../pages/vendor/products/import.page";
import { ImportHistoryPage } from "../pages/vendor/products/import-history.page";
import { ImageCropper } from "../pages/vendor/tools/image-cropper.page";
import { useSettingsApi } from "../common/hooks/use-settings-api.hook";
import { useDocumentTitle } from "../common/hooks/use-document-title.hook";
import { useVendorProfileApi } from "../common/hooks/use-vendor-profile-api.hook";
import { useAuthContext } from "../common/hooks/use-auth-context.hook";
import { OnboardingPage } from "../pages/vendor/onboarding/onboarding.page";
import { hasSkippedOnboarding, setOnboardingVendor } from "../modules/vendor/onboarding/onboarding-state";
import { JourneyFacts, isJourneyInProgress, shouldOfferJourney } from "../modules/vendor/onboarding/journey";
import { SignupStatus, useSignupApi } from "../common/hooks/use-signup-api.hook";
import { ActivationGate } from "../modules/signup/ActivationGate";
import { needsActivation, userLooksPending } from "../modules/signup/activation";
import { useWorkspaceStore } from "../modules/vendor/side-bar/workspace.store";
import Leads from "../pages/vendor/leads/leads.page";
import Timeline from "../pages/vendor/analytics/timeline.page";
import { CommandPalette } from "../modules/vendor/command-palette/CommandPalette";
import { SupportWidget } from "../modules/support/vendor/SupportWidget";
import { endSupportSessionLocally } from "../common/services/support-session.service";
import { PreviewTagSync } from "../common/services/preview-tag.service";
import { VendorUsageTracker } from "../modules/superadmin/vendor-usage/VendorUsageTracker";
import { AgentWorkToasts } from "../modules/vendor/work/AgentWorkToasts";
import { SocialBrandNudge } from "../modules/vendor/brand/SocialBrandNudge";
import EmailSettingsLayout from "../pages/vendor/engagement/email-settings-layout.page";
import ComposeEmailPage from "../pages/vendor/leads/compose-email.page";
import EmailAutomationPage from "../pages/vendor/leads/email-automation.page";
import { VisualizerStudioPage, VisualizerIntegrationRedirect } from "../pages/vendor/configurations/visualizer-studio.page";
import { MySpaces } from "../pages/vendor/spaces/my-spaces.page";
import { IntegrationPage } from "../pages/vendor/configurations/integration.page";
import { ChatSettingsPage } from "../pages/vendor/configurations/chat.page";
import BillingPage from "../pages/vendor/subscription/billing.page";
import UpgradePage from "../pages/vendor/subscription/upgrade.page";
import DevicesPage from "../pages/vendor/devices/devices.page";
import TeamPage from '../pages/vendor/team/team.page';
import CategoriesPage from "../pages/vendor/products/categories.page";
import ApiIntegrationPage from "../pages/vendor/configurations/api-integration.page";
import ContentStudioProjectsPage from "../pages/vendor/marketing/content-studio/projects.page";
import ContentStudioWorkspacePage from "../pages/vendor/marketing/content-studio/workspace.page";
import StudioHomePage from "../pages/vendor/marketing/studio/home.page";
import StudioProductionPage from "../pages/vendor/marketing/studio/production.page";
import BrandPage from "../pages/vendor/marketing/brand/brand.page";
import { MarketingOverviewPage } from "../pages/vendor/marketing/marketing-overview.page";
import { LEGACY_REDIRECTS, LastWorkspaceRedirect, LegacyRedirect } from "./legacy-redirect";
import { SocialSchedulerPage } from "../pages/vendor/marketing/social-scheduler/scheduler.page";
import { SocialCreatePostPage } from "../pages/vendor/marketing/social-scheduler/create-post.page";
import { SocialPostDetailPage } from "../pages/vendor/marketing/social-scheduler/post-detail.page";
import { MarketPage } from "../pages/vendor/market/market.page";
import { ShopPage } from "../pages/vendor/sales/shop.page";
import { RevenuePage } from "../pages/vendor/sales/revenue.page";
// feat/room-to-revenue — docs/room-to-revenue.md
import { LeadDeskPage } from "../pages/vendor/lead-desk/desk.page";
import { LeadFilePage } from "../pages/vendor/lead-desk/lead-file.page";
import { SalesInfluencePage } from "../pages/vendor/lead-desk/influence.page";
import { ShopperRequestsPage } from "../pages/vendor/shopper-requests/requests.page";
import { ShowroomQrPage } from "../pages/vendor/shopper-requests/showroom.page";
import { TradePage } from "../pages/vendor/trade/trade.page";
import { SavedRoomsPage } from "../pages/vendor/saved-rooms/rooms.page";
import { ContentPacksPage } from "../pages/vendor/content-packs/content-packs.page";
import { DecisionsPage } from "../pages/vendor/decisions/decisions.page";
import { MorningBriefPage } from "../pages/vendor/decisions/brief.page";
import { AiChannelsPage } from "../pages/vendor/ai-channels/ai.page";
import OverviewPage from "../pages/vendor/overview/overview.page";
import { AssistantOverviewPage } from "../pages/vendor/assistant/assistant-overview.page";
import { AssistantConversationsPage } from "../pages/vendor/assistant/assistant-conversations.page";
import { AssistantQuestionsPage } from "../pages/vendor/assistant/assistant-questions.page";
import { AdsOverviewPage } from "../pages/vendor/ads/ads-overview.page";
import { AdsCampaignsPage } from "../pages/vendor/ads/ads-campaigns.page";
import { AdsSuggestionsPage } from "../pages/vendor/ads/ads-suggestions.page";
import { AdsAudiencesPage } from "../pages/vendor/ads/ads-audiences.page";
import { AdsBudgetsPage } from "../pages/vendor/ads/ads-budgets.page";
import { AdsConnectionsPage } from "../pages/vendor/ads/ads-connections.page";
// The Ardealy Network — Brand Portal (manufacturer accounts)
import { BrandOverviewPage } from "../pages/vendor/brand/brand-overview.page";
import { BrandDemandPage } from "../pages/vendor/brand/brand-demand.page";
import { BrandProductPage } from "../pages/vendor/brand/brand-product.page";
import { BrandDistributionPage } from "../pages/vendor/brand/brand-distribution.page";
import { BrandUnmetPage } from "../pages/vendor/brand/brand-unmet.page";
import { BrandAnnouncementsPage } from "../pages/vendor/brand/brand-announcements.page";
import { workspacesFor } from "../modules/vendor/side-bar/workspaces";

/** How long the dashboard has to itself before the invitation appears. */
/*
 * The journey takes the whole screen, so it arrives almost at once: a
 * dashboard painted for two seconds and then covered reads as a glitch.
 */
const ONBOARDING_DELAY_MS = 150;

/**
 * Wrapper that provides productId (or "new") as a React key.
 * Forces EditProductV3Page to fully remount when navigating between
 * edit and new routes, clearing all stale form state and refs.
 */
function EditProductV3PageKeyed() {
    const { productId } = useParams();
    return <EditProductV3Page key={productId || 'new'} />;
}

export function VendorPrivateRoutes() {
    const { isDrawerOpen } = useSelector((state: VendorState) => state.ui);
    const { i18n } = useTranslation();
    const dispatch = useDispatch()
    const settingsAPI = useSettingsApi()
    // The vendor's name in the tab while signed in; the default comes back on
    // sign-out, so the next account (another vendor, a super admin) never sees it.
    const [tabTitle, setTabTitle] = useState<string | null>(null)
    useDocumentTitle(tabTitle)
    const profileAPI = useVendorProfileApi()
    const setWorkspaceGoals = useWorkspaceStore((s) => s.setGoals)
    const { user } = useAuthContext()
    const support = user?.support
    const location = useLocation()
    /*
     * A trial or a subscription that ended (docs/pricing-v2.md): the shell
     * stays, the page becomes the plan wall — except billing and the few
     * pages needed to pay or manage the team. Support sees the app as it is.
     */
    const showPlanWall = !support && readAccess((user as any)?.vendor).state === 'expired' && !isOpenWhenExpired(location.pathname)

    /*
     * The onboarding invitation, decided once per sign-in.
     *
     * `undefined` means "not yet known" and renders nothing rather than
     * flashing the app and then replacing it — the check is one request and
     * resolves before the first paint matters. A vendor who has skipped, or
     * who already has a website, never sees this again; the reminder lives on
     * as the dashboard's setup-gap card, which is computed on the server.
     */
    const [journeyFacts, setJourneyFacts] = useState<JourneyFacts | null | undefined>(undefined)
    const [journeyDone, setJourneyDone] = useState(false)

    /*
     * A self-serve account's activation ("Start for free"): until its email
     * is confirmed and the snippet is on its website, the app is replaced by
     * the "Connect your website" step. `undefined` = not known yet; `null` =
     * not self-serve, or not readable — every existing vendor, untouched.
     */
    const signupAPI = useSignupApi()
    const [signup, setSignup] = useState<SignupStatus | null | undefined>(undefined)
    /*
     * Once the activation step has been on screen it stays until the person
     * presses Continue: the status turning active mid-poll would otherwise
     * swap the app in and skip the "connected" moment.
     */
    const [gateShown, setGateShown] = useState(false)
    useEffect(() => {
        if (needsActivation(signup)) setGateShown(true)
    }, [signup])

    const needsOnboarding = journeyDone ? false : shouldOfferJourney(journeyFacts, signup === undefined ? undefined : Boolean(signup?.selfServe))

    /*
     * The invitation arrives a moment after the app does.
     *
     * Landing straight on it makes the product look like a form. Letting the
     * dashboard paint first means the vendor has seen what they signed up for
     * before being asked for anything, and the invitation reads as an offer
     * rather than a toll gate.
     */
    const [inviteVisible, setInviteVisible] = useState(false)

    // A support session ends on time even if nothing is clicked.
    useEffect(() => {
        if (!support?.expiresAt) return undefined
        const ms = new Date(support.expiresAt).getTime() - Date.now()
        const id = window.setTimeout(() => endSupportSessionLocally(), Math.max(0, ms))
        return () => window.clearTimeout(id)
    }, [support?.expiresAt])

    useEffect(() => {
        // Ardealy support is not the vendor: never invite it to onboard.
        if (support) {
            setJourneyFacts(null)
            return
        }
        /*
         * A manufacturer's account: the journey sets up a shop's visualizer on
         * its own website, which a brand selling through retailers does not
         * have. It opens on the Brand Portal instead.
         */
        if ((user as any)?.vendor?.kind === 'brand') {
            setJourneyFacts(null)
            const current = useWorkspaceStore.getState().workspace
            if (!workspacesFor('brand').some((w) => w.id === current)) useWorkspaceStore.getState().setWorkspace('brand')
            return
        }
        const vendorId = (user as any)?.vendor?._id || (user as any)?.vendor?.id
        setOnboardingVendor(vendorId)
        if (hasSkippedOnboarding(vendorId)) {
            setJourneyFacts(null)
            return
        }
        profileAPI
            .get()
            .then((profile) => {
                // The switcher's order follows what the vendor came for.
                if (Array.isArray(profile?.goals)) setWorkspaceGoals(profile.goals)
                // What decides the journey — see shouldOfferJourney.
                setJourneyFacts({ onboarded: Boolean(profile?.onboardedAt), websiteUrl: profile?.websiteUrl?.trim() || undefined, inProgress: isJourneyInProgress(vendorId) })
            })
            // A profile we cannot read is not a reason to nag.
            .catch(() => setJourneyFacts(null))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user])

    useEffect(() => {
        // Support works in the vendor's app as it is; it never sees the activation step.
        if (support) {
            setSignup(null)
            return
        }
        let live = true
        Promise.resolve(signupAPI.status())
            .then((s) => live && setSignup(s ?? null))
            // An API without the signup module, or any failure: not self-serve.
            .catch(() => live && setSignup(null))
        return () => {
            live = false
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user])

    useEffect(() => {
        // `?onboarding=…` opens it at once, whatever the profile says, so the
        // screen can be worked on without clearing the vendor's data first.
        if (window.location.search.includes('onboarding=')) {
            setInviteVisible(true)
            return undefined
        }
        if (!needsOnboarding) return undefined
        const id = window.setTimeout(() => setInviteVisible(true), ONBOARDING_DELAY_MS)
        return () => window.clearTimeout(id)
    }, [needsOnboarding])

    useEffect(() => {
        const getConfig = async () => {
            try {
                const companyConfig: Config = await settingsAPI.findConfig()
                if (!companyConfig) {
                    console.error('Company config is undefined');
                    return;
                }

                const defaultLanguage = companyConfig.language?.[0] ?? 'en';
                i18n.changeLanguage(defaultLanguage);
                dispatch(setPrimaryColor(companyConfig.primaryColor))
                dispatch(setSecondaryColor(companyConfig.secondaryColor))
                dispatch(setTextSecondaryColor(companyConfig.textSecondaryColor))
                dispatch(setLogo(companyConfig.logo))
                dispatch(setFont("Lexend"))
                dispatch(setLanguage(defaultLanguage))
                dispatch(setCompanyName(companyConfig.company))
                dispatch(setInitialTheme())
                dispatch(setProductsOrder(companyConfig.preferedProductsOrder??"DATE+1"))
                dispatch(setOrderButtonLabel(companyConfig.orderButtonLabel ?? "Order"))
                dispatch(setOrderButtonAction(companyConfig.orderButtonAction ?? "link"))
                // Absent means measurement is on: only an explicit false is an opt-out.
                dispatch(setAnalyticsEnabled(companyConfig.analyticsEnabled !== false))
                setTabTitle((companyConfig.company ?? 'Company') + ' - Visualizer Backoffice');
            } catch (error) {
                console.error('Error loading company config:', error);
            }
        }
        getConfig()
    },[])

    /*
     * The activation step instead of the app, never over it: the app's pages
     * would only meet "finish setting up first" refusals. A user record that
     * already says pending holds the app back until the status is read, so it
     * is never painted and then covered.
     */
    if (!support && (needsActivation(signup) || (gateShown && signup) || (signup === undefined && userLooksPending(user)))) {
        return (
            <div dir={i18n.language === "ar" ? "rtl" : "ltr"}>
                {signup ? (
                    <ActivationGate
                        status={signup}
                        onStatus={setSignup}
                        onContinue={() => {
                            setGateShown(false)
                            setSignup({ ...signup, status: 'active' })
                        }}
                    />
                ) : (
                    <div className="fixed inset-0 bg-background" />
                )}
            </div>
        )
    }

    /** A self-serve account gave its website at signup: the journey does not ask again. */
    const knownWebsite = signup?.selfServe
        ? journeyFacts?.websiteUrl || (signup.domain ? `https://${signup.domain}` : undefined)
        : undefined

    return (
        <div dir={i18n.language === "ar" ? "rtl": "ltr"}>
                    <SidebarProvider className="h-screen min-h-0 w-full">
                        <SideBar/>
                        <SidebarInset
                            /* The docked assistant is a sticky column in here and has to be
                               exactly this tall — a sticky box taller than what it scrolls in
                               cannot stick. Shared so the two cannot drift apart. */
                            style={{ height: SHELL_HEIGHT }}
                            className={cn('flex-row items-start overflow-auto', isDrawerOpen && 'invisible')}
                        >
                          {/*
                            * The assistant lives INSIDE the scrolling column, as a
                            * sticky sibling of the page rather than a layer over it.
                            *
                            * That is what keeps the scrollbar at the window's edge. A
                            * fixed panel meant this column had to be narrowed to make
                            * room, which moved its scrollbar 460px inland — a scrollbar
                            * floating in the middle of the screen. As a column in here,
                            * the scroller still reaches the right edge and the panel
                            * simply sits beside it.
                            */}
                          {/* Full width on purpose. The reading measure is applied to page
                                content inside PageContainer, so the top bar can span the
                                window while the page below it stays centred. */}
                            {/* minWidth:0 so a wide table shrinks with the column
                                instead of pushing the assistant off the edge. */}
                            <div className="min-w-0 flex-grow">
                            {showPlanWall ? <TrialEndedWall /> : (
                            <Routes>
                                {/*
                                  One rule for every address: /{workspace}/{page}, with a
                                  page's tabs in ?tab= — see workspaces.ts. The catalogue is
                                  the one thing every workspace shares, so it keeps /products.
                                  Everything that moved is redirected at the bottom.
                                */}

                                {/* ── Home ───────────────────────────────────────── */}
                                <Route path="home">
                                    <Route path="" element={<Navigate to="/home/dashboard" replace />} />
                                    {/* Talk to the team: the agent-first front door. */}
                                    <Route path="dashboard" element={<AgentHomePage />} />
                                    <Route path="chat" element={<AgentChatPage />} />
                                    <Route path="chat/:threadUuid" element={<AgentChatPage />} />
                                    <Route path="agents" element={<AgentsPage />} />
                                    {/* The previous Home - every card and widget - for whoever wants the charts. */}
                                    <Route path="overview" element={<Dashboard />} />
                                    {/* The website connection every feature runs from. */}
                                    <Route path="integration" element={<IntegrationPage scope="website" />} />
                                    <Route path="billing" element={<Navigate to="/home/billing/plan" replace />} />
                                    <Route path="billing/plan" element={<BillingPage />} />
                                    <Route path="billing/upgrade" element={<UpgradePage />} />
                                    {/* Seats and the devices signed in to the account. */}
                                    <Route path="devices" element={<DevicesPage />} />
                                    {/* Who can sign in, and inviting more. */}
                                    <Route path="team" element={<TeamPage />} />
                                    {/* Ardi outside the chat box: the chat apps it answers on, what it runs on a schedule. */}
                                    <Route path="channels" element={<ChannelsPage />} />
                                    <Route path="routines" element={<RoutinesPage />} />
                                    {/* One inbox of what waits for a yes, and the brief that carries it (feat/room-to-revenue). */}
                                    <Route path="decisions" element={<DecisionsPage />} />
                                    <Route path="brief" element={<MorningBriefPage />} />
                                    {/* Every notification, and what reaches each person where (?tab=settings). */}
                                    <Route path="notifications" element={<NotificationsPage portal="vendor" />} />
                                </Route>

                                {/* ── Visualizer ─────────────────────────────────── */}
                                <Route path="visualizer">
                                    <Route path="" element={<Navigate to="/visualizer/dashboard" replace />} />
                                    <Route path="dashboard" element={<OverviewPage />} />
                                    <Route path="engagement" element={<Navigate to="/visualizer/engagement/leads" replace />} />
                                    <Route path="engagement/leads" element={<Leads />} />
                                    {/* Tabs: ?tab=overview | products */}
                                    <Route path="engagement/analytics" element={<AnalyticsPage />} />
                                    <Route path="engagement/activity" element={<Timeline />} />
                                    {/* The studio: ?tab=general | advanced | product-display | features | matching | button */}
                                    <Route path="settings" element={<VisualizerStudioPage />} />
                                    <Route path="spaces" element={<MySpaces />} />
                                    {/* Tabs: ?tab=setup | matching | features | button */}
                                    {/* Folded into the studio; keeps ?tab=matching|features|button. */}
                                    <Route path="integration" element={<VisualizerIntegrationRedirect />} />
                                    {/* The old design page; its settings are Brand in the studio now. */}
                                    <Route path="design" element={<Navigate to="/visualizer/settings?tab=general" replace />} />
                                </Route>

                                {/* ── Shopping Assistant ─────────────────────────── */}
                                <Route path="assistant">
                                    <Route path="" element={<Navigate to="/assistant/dashboard" replace />} />
                                    <Route path="dashboard" element={<AssistantOverviewPage />} />
                                    <Route path="conversations" element={<AssistantConversationsPage />} />
                                    <Route path="questions" element={<AssistantQuestionsPage />} />
                                    {/* Tabs: ?tab=general | appearance | questions | photos */}
                                    <Route path="settings" element={<ChatSettingsPage />} />
                                </Route>

                                {/* ── Marketing ──────────────────────────────────── */}
                                <Route path="marketing">
                                    <Route path="" element={<Navigate to="/marketing/dashboard" replace />} />
                                    <Route path="dashboard" element={<MarketingOverviewPage />} />
                                    <Route path="brand" element={<BrandPage />} />
                                    <Route path="content-studio" element={<StudioHomePage />} />
                                    <Route path="content-studio/projects" element={<ContentStudioProjectsPage />} />
                                    <Route path="content-studio/brand-kit" element={<Navigate to="/marketing/brand" replace />} />
                                    <Route path="content-studio/:projectUuid" element={<StudioProductionPage />} />
                                    <Route path="content-studio/:projectUuid/canvas" element={<ContentStudioWorkspacePage />} />
                                    {/* Pictures, a clip and posts per product, waiting for a yes (feat/room-to-revenue). */}
                                    <Route path="content-packs" element={<ContentPacksPage />} />
                                    <Route path="email" element={<Navigate to="/marketing/email/compose" replace />} />
                                    <Route path="email/compose" element={<ComposeEmailPage />} />
                                    <Route path="email/automation" element={<EmailAutomationPage />} />
                                    {/* Tabs: ?tab=templates | smtp */}
                                    <Route path="email/settings" element={<EmailSettingsLayout />} />
                                    {/* Tabs: ?tab=calendar | posts | accounts */}
                                    <Route path="social-scheduler" element={<SocialSchedulerPage />} />
                                    <Route path="social-scheduler/new" element={<SocialCreatePostPage />} />
                                    <Route path="social-scheduler/post/:uuid" element={<SocialPostDetailPage />} />
                                </Route>

                                {/* ── Ads ────────────────────────────────────────── */}
                                <Route path="ads">
                                    <Route path="" element={<Navigate to="/ads/dashboard" replace />} />
                                    <Route path="dashboard" element={<AdsOverviewPage />} />
                                    <Route path="campaigns" element={<AdsCampaignsPage />} />
                                    {/* Tabs: ?tab=open | applied | dismissed */}
                                    <Route path="suggestions" element={<AdsSuggestionsPage />} />
                                    <Route path="audiences" element={<AdsAudiencesPage />} />
                                    <Route path="budgets" element={<AdsBudgetsPage />} />
                                    <Route path="connections" element={<AdsConnectionsPage />} />
                                </Route>

                                {/* ── Business ───────────────────────────────────── */}
                                {/* Every Market page is a view of one read; see VendorMarketController. */}
                                <Route path="business">
                                    <Route path="" element={<Navigate to="/business/dashboard" replace />} />
                                    <Route path="dashboard" element={<MarketPage view="overview" />} />
                                    <Route path="visibility" element={<Navigate to="/business/visibility/search" replace />} />
                                    <Route path="visibility/search" element={<MarketPage view="search" />} />
                                    <Route path="visibility/profile" element={<MarketPage view="profile" />} />
                                    <Route path="visibility/backlinks" element={<MarketPage view="backlinks" />} />
                                    <Route path="visibility/health" element={<MarketPage view="health" />} />
                                    <Route path="competitors" element={<Navigate to="/business/competitors/rivals" replace />} />
                                    <Route path="competitors/rivals" element={<MarketPage view="competitors" />} />
                                    <Route path="competitors/gaps" element={<MarketPage view="gaps" />} />
                                    {/* What sold, and the shop it is read from. */}
                                    <Route path="sales" element={<Navigate to="/business/sales/revenue" replace />} />
                                    <Route path="sales/revenue" element={<RevenuePage />} />
                                    <Route path="sales/shop" element={<ShopPage />} />
                                    {/* Being found by AI assistants: MCP, the catalogue feed, AI visibility. ?tab=visibility | assistants */}
                                    <Route path="ai" element={<AiChannelsPage />} />
                                </Route>

                                {/* ── Brand Portal — manufacturer accounts (the Ardealy Network) ── */}
                                {/* Each page checks the account is a brand before asking the API anything. */}
                                <Route path="brand">
                                    <Route path="" element={<Navigate to="/brand/overview" replace />} />
                                    <Route path="overview" element={<BrandOverviewPage />} />
                                    <Route path="demand" element={<BrandDemandPage />} />
                                    <Route path="products/:uuid" element={<BrandProductPage />} />
                                    <Route path="distribution" element={<BrandDistributionPage />} />
                                    <Route path="unmet" element={<BrandUnmetPage />} />
                                    <Route path="announcements" element={<BrandAnnouncementsPage />} />
                                </Route>

                                {/* ── Sales — the lead desk (feat/room-to-revenue) ── */}
                                <Route path="sales">
                                    <Route path="" element={<Navigate to="/sales/desk" replace />} />
                                    <Route path="desk" element={<LeadDeskPage />} />
                                    <Route path="leads/:leadId" element={<LeadFilePage />} />
                                    <Route path="requests" element={<ShopperRequestsPage />} />
                                    <Route path="influence" element={<SalesInfluencePage />} />
                                    <Route path="trade" element={<TradePage />} />
                                    <Route path="showroom" element={<ShowroomQrPage />} />
                                    <Route path="rooms" element={<SavedRoomsPage />} />
                                </Route>

                                {/* ── Products — shared by every workspace ─────────── */}
                                <Route path='products'>
                                    <Route path='catalog' element={<MyProducts />} />
                                    <Route path='categories' element={<CategoriesPage />} />
                                    {/* The Ardealy Network, retailer side: matches, brands to add from, data sharing. */}
                                    <Route path='network' element={<NetworkPage />} />
                                    <Route path='network/brands/:slug' element={<NetworkBrandPage />} />
                                    <Route path='seamless-generator' element={<SeamlessGenerator />} />
                                    {/* V3 Editor - primary routes (keyed to force remount on navigation) */}
                                    <Route path='new' element={<EditProductV3PageKeyed />} />
                                    <Route path=':productId/edit' element={<EditProductV3PageKeyed />} />
                                    <Route path='v3/new' element={<EditProductV3PageKeyed />} />
                                    <Route path='v3/:productId' element={<EditProductV3PageKeyed />} />
                                    {/* Legacy V1/V2 routes kept for backward compatibility */}
                                    <Route path='new-v2' element={<EditProductV3PageKeyed />} />
                                    <Route path=':productId/edit-v2' element={<EditProductV3PageKeyed />} />
                                    <Route path="import" element={<ImportProductsV2 />} />
                                    <Route path="import-history" element={<ImportHistoryPage />} />
                                    <Route path="sync" element={<ApiIntegrationPage />} />
                                </Route>

                                {/* Tools reached from inside the product editor, not the rail. */}
                                <Route path="configuration/tools">
                                    <Route path="" element={<SeamlessGenerator />} />
                                    <Route path="seamless-generator" element={<SeamlessGenerator />} />
                                    <Route path="image-cropper" element={<ImageCropper />} />
                                </Route>

                                {/*
                                  Every address that moved. Bookmarks, emails, Stripe's return
                                  URL and OAuth callbacks still use them; the redirect keeps
                                  their query string.
                                */}
                                {/* Sign-in lands here; a vendor goes on to the workspace they last used. */}
                                <Route path="dashboard" element={<LastWorkspaceRedirect />} />
                                {LEGACY_REDIRECTS.map(({ from, to }) => (
                                    <Route key={from} path={from} element={<LegacyRedirect to={to} />} />
                                ))}

                                <Route path="*" element={<></>} />
                            </Routes>
                            )}
                          </div>
                            <AskAiPanel />
                            <CommandPalette />
                            {/* Chat with Ardealy support; opened from the top bar. */}
                            {/* Support's own chat widget would talk to support. */}
                            {!support && <SupportWidget />}
                            <PreviewTagSync />
                            {/* Which pages the team opens (super admin › Vendor usage). */}
                            <VendorUsageTracker />
                            {/* Mounted once: the only extra poller of agent
                                work, and the thing that tells everyone else
                                what is running. */}
                            <AgentWorkToasts />
                            <SocialBrandNudge />
                            {inviteVisible && (
                                <OnboardingPage
                                    knownWebsite={knownWebsite}
                                    websiteSaved={Boolean(journeyFacts?.websiteUrl)}
                                    onDone={() => {
                                        setInviteVisible(false)
                                        setJourneyDone(true)
                                    }}
                                />
                            )}
                        </SidebarInset>
                    </SidebarProvider>
        </div>
    );
}
